import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import { CerebroLink, CerebroText, CerebroTextButton } from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { useActiveNdaQuery } from '../../shared/hooks/Queries/useActiveNdaQuery';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { StandardNda } from '../LenderNda/StandardNda';
import { sanitize } from 'dompurify';
import { getApiResponseData } from '../../shared/utils/axios';

interface AccessNdaRequestDialogProps {
	open: boolean;
	roomUserGuid: string | undefined;
	dataRoomGuid: string | undefined;
	accept: () => void;
	close: () => void;
}

export const AccessNdaRequestDialog: React.FC<AccessNdaRequestDialogProps> = ({
	open,
	roomUserGuid,
	dataRoomGuid,
	accept,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	var activeNdaQuery = useActiveNdaQuery(dataRoomGuid);
	const onAcceptNda = () => acceptNdaMutation.mutate();

	const acceptNdaMutation = useMutation(
		async () => {
			const templateGuid =
				activeNdaQuery.data?.ndaType == 'Template' ? activeNdaQuery.data?.lenderTemplateGuid : null;
			const appendUrl = templateGuid ? `?lenderTemplateGuid=${templateGuid}` : '';

			const { data, error } = await getApiResponseData<void>(
				api.post(CerebroApiEndpoints.LenderNda.Root(dataRoomGuid) + appendUrl)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				QueryKeys.LenderNda.Root(dataRoomGuid);
				snackbar.enqueueSnackbar('NDA Accepted.', { variant: 'success' });
				accept();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	return (
		<>
			{activeNdaQuery.isSuccess && (
				<Dialog open={open} onClose={close} maxWidth={'lg'}>
					<DialogTitle>Data Room NDA</DialogTitle>
					<DialogContent>
						{activeNdaQuery.data.ndaType == 'DealSpecific' && (
							<>
								<CerebroText sx={{ marginTop: 3, marginBottom: 6 }}>
									You have a custom NDA for access to this data room. Click below to acknowledge and
									continue to the data room.
								</CerebroText>
								<CerebroLink
									sx={{ display: 'block', marginBottom: 2, textAlign: 'right' }}
									href={activeNdaQuery.data.url}
									variant="small"
									target="_blank"
								>
									View Agreement
								</CerebroLink>
							</>
						)}

						{activeNdaQuery.data.ndaType == 'Template' && (
							<CerebroText>{activeNdaQuery.data.agreementText}</CerebroText>
						)}

						{activeNdaQuery.data.ndaType == 'BankSpecific' && (
							<>
								<CerebroText sx={{ marginTop: 3, marginBottom: 6 }}>
									You bank is under NDA with Cerebro Capital. Please click the link below to review
									the conditions of access.
								</CerebroText>
								<CerebroLink
									sx={{ display: 'block', marginBottom: 2, textAlign: 'right' }}
									href={activeNdaQuery.data.url}
									variant="small"
									target="_blank"
								>
									View Agreement
								</CerebroLink>
							</>
						)}

						{activeNdaQuery.data.ndaType == 'Custom' && (
							<div
								dangerouslySetInnerHTML={{ __html: sanitize(activeNdaQuery.data.agreementText ?? '') }}
							></div>
						)}

						{activeNdaQuery.data.ndaType == 'Standard' && (
							<StandardNda dataRoomGuid={dataRoomGuid} companyName={activeNdaQuery.data.companyName} />
						)}
					</DialogContent>
					<DialogActions>
						<CerebroTextButton onClick={close}>Decline</CerebroTextButton>
						<CerebroTextButton onClick={onAcceptNda}>Accept</CerebroTextButton>
					</DialogActions>
				</Dialog>
			)}
		</>
	);
};
